import { create } from 'zustand';
import {
  registerPlayer as registerPlayerService,
  unregisterPlayer as unregisterPlayerService,
  getNotifications as getNotificationsService,
  markNotificationAsRead as markNotificationAsReadService,
  createNotification as createNotificationService,
  createMultipleNotifications as createMultipleNotificationsService,
} from '../services/notification.service';
import {
  Notification,
  NotificationsResponse,
  RegisterPlayerRequest,
  GetNotificationsParams,
  CreateNotificationRequest,
  CreateMultipleNotificationsRequest,
} from '../types/notification.type';

interface NotificationState {
  notifications: Notification[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  unreadCount: number;
  currentPlayerId: string | null;
  
  // Actions
  registerPlayer: (data: RegisterPlayerRequest) => Promise<boolean>;
  unregisterPlayer: (playerId: string) => Promise<boolean>;
  fetchNotifications: (
    organizationId: string,
    params?: GetNotificationsParams
  ) => Promise<void>;
  markAsRead: (
    organizationId: string,
    notificationId: string
  ) => Promise<boolean>;
  createNotification: (data: CreateNotificationRequest) => Promise<Notification | null>;
  createMultipleNotifications: (
    data: CreateMultipleNotificationsRequest
  ) => Promise<Notification[]>;
  updateUnreadCount: () => void;
  clearNotifications: () => void;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  totalCount: 0,
  isLoading: false,
  error: null,
  unreadCount: 0,
  currentPlayerId: null,

  registerPlayer: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await registerPlayerService(data);
      const result = response as GenericResponse<{ success: boolean; player_id: string }>;
      
      set({
        currentPlayerId: result.result.player_id,
        isLoading: false,
      });
      
      return true;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to register player',
        isLoading: false,
      });
      return false;
    }
  },

  unregisterPlayer: async (playerId) => {
    set({ isLoading: true, error: null });
    try {
      await unregisterPlayerService(playerId);
      set({
        currentPlayerId: null,
        isLoading: false,
      });
      return true;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to unregister player',
        isLoading: false,
      });
      return false;
    }
  },

  fetchNotifications: async (organizationId, params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getNotificationsService(organizationId, params);
      
      // Handle both wrapped and direct response formats
      let notificationsData: NotificationsResponse;
      if ('result' in response && response.result) {
        // Wrapped in GenericResponse
        const data = response as GenericResponse<NotificationsResponse>;
        notificationsData = data.result;
      } else {
        // Direct response
        notificationsData = response as NotificationsResponse;
      }
      
      set({
        notifications: notificationsData.result_list,
        totalCount: notificationsData.total_count,
        isLoading: false,
      });
      
      // Update unread count
      get().updateUnreadCount();
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch notifications',
        isLoading: false,
      });
    }
  },

  markAsRead: async (organizationId, notificationId) => {
    try {
      const response = await markNotificationAsReadService(
        organizationId,
        notificationId
      );
      const data = response as GenericResponse<Notification>;
      
      // Update notification in list
      set((state) => ({
        notifications: state.notifications.map((notif) =>
          notif.id === notificationId ? data.result : notif
        ),
      }));
      
      // Update unread count
      get().updateUnreadCount();
      
      return true;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to mark notification as read',
      });
      return false;
    }
  },

  createNotification: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createNotificationService(data);
      const result = response as GenericResponse<Notification>;
      set({ isLoading: false });
      return result.result;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to create notification',
        isLoading: false,
      });
      return null;
    }
  },

  createMultipleNotifications: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createMultipleNotificationsService(data);
      const result = response as GenericResponse<Notification[]>;
      set({ isLoading: false });
      return result.result;
    } catch (error: any) {
      set({
        error:
          error.response?.data?.detail || 'Failed to create notifications',
        isLoading: false,
      });
      return [];
    }
  },

  updateUnreadCount: () => {
    const { notifications } = get();
    const unreadCount = notifications.filter((notif) => !notif.is_read).length;
    set({ unreadCount });
  },

  clearNotifications: () => {
    set({
      notifications: [],
      totalCount: 0,
      unreadCount: 0,
      error: null,
    });
  },
}));

